import { Form, Formik, type FormikErrors, type FormikHelpers } from "formik";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createDependent, updateDependent } from "@/features/clients/api";
import { CheckboxField } from "@/features/clients/components/record-form-fields";
import { FIELD_LABEL } from "@/features/clients/components/profile-form-shared";
import type { Dependent, DependentPayload } from "@/features/clients/types";
import { applyApiErrors } from "@/lib/form-errors";

interface DependentFormValues {
  name: string;
  age: string;
  relationship: string;
  claimedOnReturn: boolean;
  contributesToHousehold: boolean;
}

const validationSchema = Yup.object({
  name: Yup.string().trim().required("Name is required"),
  age: Yup.string()
    .matches(/^\d*$/, "Enter a whole number")
    .test("range", "Age must be between 0 and 130", (v) => {
      if (!v) return true;
      const n = Number(v);
      return n >= 0 && n <= 130;
    }),
  relationship: Yup.string(),
});

function toInitialValues(dependent: Dependent | null): DependentFormValues {
  return {
    name: dependent?.name ?? "",
    age: dependent?.age != null ? String(dependent.age) : "",
    relationship: dependent?.relationship ?? "",
    claimedOnReturn: dependent?.claimedOnReturn ?? true,
    contributesToHousehold: dependent?.contributesToHousehold ?? false,
  };
}

function toPayload(v: DependentFormValues): DependentPayload {
  return {
    name: v.name.trim(),
    age: v.age === "" ? null : Number(v.age),
    relationship: v.relationship,
    claimed_on_return: v.claimedOnReturn,
    contributes_to_household: v.contributesToHousehold,
  };
}

interface DependentFormDialogProps {
  clientId: string;
  /** `null` = add; otherwise the dialog edits this dependent. */
  dependent: Dependent | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires after a successful save so the section can refetch. */
  onSaved: () => void;
}

export function DependentFormDialog({
  clientId,
  dependent,
  open,
  onOpenChange,
  onSaved,
}: DependentFormDialogProps) {
  const editing = dependent !== null;

  async function handleSubmit(
    values: DependentFormValues,
    bag: FormikHelpers<DependentFormValues>,
  ) {
    try {
      if (editing) {
        await updateDependent(clientId, dependent.id, toPayload(values));
      } else {
        await createDependent(clientId, toPayload(values));
      }
      toast.success(editing ? "Dependent updated" : "Dependent added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      const setErrors = bag.setErrors as (errors: FormikErrors<never>) => void;
      toast.error(
        applyApiErrors(err, { setErrors }, { fallback: "Couldn't save dependent." }),
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Dependent" : "Add Dependent"}</DialogTitle>
        </DialogHeader>
        <Formik
          initialValues={toInitialValues(dependent)}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="grid gap-4">
              <FormField name="name" label="Name" labelClassName={FIELD_LABEL} />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  name="age"
                  label="Age"
                  labelClassName={FIELD_LABEL}
                  inputMode="numeric"
                />
                <FormField
                  name="relationship"
                  label="Relationship"
                  labelClassName={FIELD_LABEL}
                  placeholder="e.g. Daughter"
                />
              </div>
              <CheckboxField name="claimedOnReturn" label="Claimed on tax return" />
              <CheckboxField
                name="contributesToHousehold"
                label="Contributes to household income"
              />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {editing ? "Save Changes" : "Add Dependent"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
